import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Eye, EyeOff, X, Save, FileText, Clock, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../config';


export const AdminBlogManager = () => {
  const { getToken } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchPosts = () => {
    axios.get(`${API}/admin/blog`, { headers }).then(r => setPosts(r.data.posts || [])).catch(() => {});
  };

  useEffect(() => { fetchPosts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', tags: '', meta_title: '', meta_description: '', published: false, lang: 'en', scheduled_publish_at: '' });

  const openNew = () => {
    setForm({ title: '', slug: '', excerpt: '', content: '', tags: '', meta_title: '', meta_description: '', published: false, lang: 'en', scheduled_publish_at: '' });
    setEditing('new');
  };

  const openEdit = (post) => {
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      tags: (post.tags || []).join(', '),
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      published: post.published || false,
      lang: post.lang || 'en',
      scheduled_publish_at: post.scheduled_publish_at ? post.scheduled_publish_at.slice(0, 16) : '',
    });
    setEditing(post);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      scheduled_publish_at: form.scheduled_publish_at ? new Date(form.scheduled_publish_at).toISOString() : null,
    };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/blog`, payload, { headers });
        toast.success('Article created');
      } else {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, { headers });
        toast.success('Article updated');
      }
      setEditing(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers });
      toast.success('Article deleted');
      fetchPosts();
    } catch {
      toast.error('Error deleting article');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, { headers });
      fetchPosts();
    } catch {
      toast.error('Error toggling publish');
    }
  };

  const publishScheduled = async () => {
    try {
      const res = await axios.post(`${API}/admin/blog/publish-scheduled`, {}, { headers });
      toast.success(`${res.data.published_count} article(s) published`);
      fetchPosts();
    } catch {
      toast.error('Error publishing scheduled articles');
    }
  };

  // Editor view
  if (editing) {
    return (
      <div className="space-y-4" data-testid="admin-blog-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-foreground font-medium">{editing === 'new' ? 'New Article' : 'Edit Article'}</h3>
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setEditing(null)} data-testid="admin-blog-cancel">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Title</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-title-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Slug</Label>
            <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              placeholder="auto-generated" className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-slug-input" />
          </div>
        </div>
        <div>
          <Label className="text-muted-foreground text-xs mb-1 block">Excerpt</Label>
          <Input value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
            className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-excerpt-input" />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs mb-1 block">Content (supports ## headings, - lists)</Label>
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            rows={12} className="w-full bg-secondary border border-border text-foreground rounded-lg text-sm p-3 outline-none focus:border-blue-500 resize-y"
            data-testid="admin-blog-content-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Tags (comma-separated)</Label>
            <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-tags-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Language</Label>
            <select value={form.lang} onChange={e => setForm(f => ({ ...f, lang: e.target.value }))}
              className="w-full bg-secondary border border-border text-foreground h-9 rounded-lg text-sm px-3 outline-none" data-testid="admin-blog-lang-select">
              <option value="en">EN</option><option value="fr">FR</option><option value="es">ES</option><option value="de">DE</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                className="accent-blue-500" data-testid="admin-blog-published-check" />
              <span className="text-sm text-muted-foreground">Published</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Meta Title (SEO)</Label>
            <Input value={form.meta_title} onChange={e => setForm(f => ({ ...f, meta_title: e.target.value }))}
              className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-meta-title-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block">Meta Description (SEO)</Label>
            <Input value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
              className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-meta-desc-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Schedule publication</Label>
            <Input type="datetime-local" value={form.scheduled_publish_at}
              onChange={e => setForm(f => ({ ...f, scheduled_publish_at: e.target.value }))}
              className="bg-secondary border-border text-foreground h-9 rounded-lg text-sm" data-testid="admin-blog-schedule-input" />
          </div>
        </div>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6" data-testid="admin-blog-save-btn">
          <Save className="w-4 h-4 mr-2" />Save
        </Button>
      </div>
    );
  }

  // List view
  return (
    <div data-testid="admin-blog-list">
      <div className="flex items-center justify-between mb-4">
        <span className="text-muted-foreground text-sm">{posts.length} article(s)</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-border text-foreground/80 hover:text-white rounded-lg" onClick={publishScheduled} data-testid="admin-blog-publish-scheduled-btn">
            <Clock className="w-4 h-4 mr-1" />Publish scheduled
          </Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg" onClick={openNew} data-testid="admin-blog-new-btn">
            <Plus className="w-4 h-4 mr-1" />New
          </Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <p className="text-muted-foreground text-sm py-3">No articles yet.</p>
      ) : (
        <div className="space-y-2">
          {posts.map((post) => (
            <div key={post.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 group" data-testid={`admin-blog-row-${post.id}`}>
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{post.title}</p>
                <p className="text-xs text-muted-foreground">
                  {post.slug} · {post.lang?.toUpperCase()}
                  {post.scheduled_publish_at && !post.published && (
                    <span className="ml-2 text-amber-400"><Clock className="w-3 h-3 inline mr-0.5" />{new Date(post.scheduled_publish_at).toLocaleString()}</span>
                  )}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${post.published ? 'bg-emerald-500/10 text-emerald-400' : post.scheduled_publish_at ? 'bg-amber-500/10 text-amber-400' : 'bg-zinc-700/50 text-muted-foreground'}`}>
                {post.published ? 'Published' : post.scheduled_publish_at ? 'Scheduled' : 'Draft'}
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => togglePublish(post)} className="p-1.5 rounded hover:bg-zinc-700" title={post.published ? 'Unpublish' : 'Publish'}>
                  {post.published ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-emerald-400" />}
                </button>
                <button onClick={() => openEdit(post)} className="p-1.5 rounded hover:bg-zinc-700" data-testid={`admin-blog-edit-${post.id}`}>
                  <Edit3 className="w-3.5 h-3.5 text-blue-400" />
                </button>
                <button onClick={() => handleDelete(post.id)} className="p-1.5 rounded hover:bg-zinc-700" data-testid={`admin-blog-delete-${post.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
